/**
 * Allowlisted SPA paths for orchestration `app_tab_link` / `app_tab_links` payloads.
 * Must match DigitalRx TabsNavLinks / HashRouter routes.
 */
export const ALLOWED_APP_TAB_PATHS = [
  "/medicationshistory",
  "/savedprescriptions",
] as const;

export type AllowedAppTabPath = (typeof ALLOWED_APP_TAB_PATHS)[number];

export interface AppTabLink {
  path: string;
  label: string;
}

export function isAllowedAppTabPath(path: unknown): path is AllowedAppTabPath {
  return (
    typeof path === "string" &&
    (ALLOWED_APP_TAB_PATHS as readonly string[]).includes(path)
  );
}

/** Return an allowlisted tab link, or null if the payload is unsafe/incomplete. */
export function normalizeAppTabLink(raw: unknown): AppTabLink | null {
  if (!raw || typeof raw !== "object") return null;
  const d = raw as Record<string, unknown>;
  const path = typeof d.path === "string" ? d.path.trim() : "";
  if (!isAllowedAppTabPath(path)) return null;
  let label = typeof d.label === "string" ? d.label.trim() : "";
  if (!label) {
    label =
      path === "/medicationshistory" ? "Medication History" : "Saved Prescriptions";
  }
  return { path, label };
}

/** Normalize `app_tab_links` (list) or legacy single `app_tab_link` into allowlisted links. */
export function normalizeAppTabLinks(raw: unknown): AppTabLink[] {
  if (Array.isArray(raw)) {
    const out: AppTabLink[] = [];
    const seen = new Set<string>();
    for (const item of raw) {
      const link = normalizeAppTabLink(item);
      if (link && !seen.has(link.path)) {
        seen.add(link.path);
        out.push(link);
      }
    }
    return out;
  }
  const single = normalizeAppTabLink(raw);
  return single ? [single] : [];
}
